const LEARNING_STATUSES = new Set(['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED'])
const NEXT_TYPES = new Set(['STAGE', 'KNOWLEDGE_POINT', 'COURSE_COMPLETED'])

const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0
const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value)

const normalizeItems = (items) => Array.isArray(items)
  ? items.filter(item => item && isFiniteNumber(item.id) && isNonEmptyString(item.type) && isNonEmptyString(item.name))
  : []

const normalizePreviousLearning = (value) => {
  if (!value || !isFiniteNumber(value.knowledgePointId) || !isNonEmptyString(value.knowledgePointName)) return null

  const assessmentAvailable = value.assessmentAvailable === true
  return {
    lastLearnedAt: isNonEmptyString(value.lastLearnedAt) ? value.lastLearnedAt : '',
    knowledgePointId: value.knowledgePointId,
    knowledgePointName: value.knowledgePointName,
    learnedItems: normalizeItems(value.learnedItems),
    masteredItems: assessmentAvailable ? normalizeItems(value.masteredItems) : [],
    needsPracticeItems: assessmentAvailable ? normalizeItems(value.needsPracticeItems) : [],
    assessmentAvailable
  }
}

const normalizeTodayPlan = (value) => {
  if (!value || !isNonEmptyString(value.goal)) return null

  return {
    goal: value.goal,
    reason: isNonEmptyString(value.reason) ? value.reason : '',
    steps: normalizeItems(value.steps).map(step => ({
      ...step,
      order: isFiniteNumber(step.order) ? step.order : null,
      estimatedMinutes: isFiniteNumber(step.estimatedMinutes) ? step.estimatedMinutes : null
    })),
    totalEstimatedMinutes: isFiniteNumber(value.totalEstimatedMinutes) ? value.totalEstimatedMinutes : null
  }
}

export function buildLearningContextKey ({ studentId, knowledgePointId, language }) {
  return [studentId, knowledgePointId, language].map(value => String(value ?? '')).join('|')
}

export function isLearningContextData (value) {
  if (!value || typeof value !== 'object') return false
  if (!LEARNING_STATUSES.has(value.status)) return false
  if (!value.course || !isNonEmptyString(value.course.knowledgePointName)) return false
  if (!isNonEmptyString(value.course.subjectName) || !isFiniteNumber(value.course.difficultyLevel)) return false
  if (!value.progress || !isFiniteNumber(value.progress.completedStages) ||
    !isFiniteNumber(value.progress.totalStages) || !isFiniteNumber(value.progress.percentage)) return false
  if (!value.next || !NEXT_TYPES.has(value.next.type)) return false
  if (value.status !== 'COMPLETED' && !value.current) return false
  if (value.current && (!isNonEmptyString(value.current.stageName) ||
    !isFiniteNumber(value.current.stageIndex) || !isFiniteNumber(value.current.totalStages))) return false
  return true
}

const fallbackModel = () => ({
  state: 'fallback',
  titleKey: 'chat.learningWelcome.fallbackTitle',
  bodyKey: 'chat.learningWelcome.fallbackBody',
  nextKey: '',
  params: {},
  percentage: 0,
  showProgress: false,
  previousLearning: null,
  todayPlan: null
})

export function buildLearningWelcomeModel (context) {
  if (!isLearningContextData(context)) return fallbackModel()

  const percentage = Math.max(0, Math.min(100, Math.round(context.progress.percentage)))
  const enhancements = {
    previousLearning: normalizePreviousLearning(context.previousLearning),
    todayPlan: normalizeTodayPlan(context.todayPlan)
  }
  const params = {
    knowledgePointName: context.course.knowledgePointName,
    subjectName: context.course.subjectName,
    currentStageName: context.current?.stageName || '',
    completedStages: context.progress.completedStages,
    totalStages: context.progress.totalStages,
    percentage,
    nextName: context.next.name || ''
  }

  if (context.status === 'NOT_STARTED') {
    return {
      state: 'notStarted',
      titleKey: 'chat.learningWelcome.notStartedTitle',
      bodyKey: 'chat.learningWelcome.notStartedBody',
      nextKey: '',
      params,
      percentage,
      showProgress: false,
      ...enhancements
    }
  }

  if (context.status === 'IN_PROGRESS') {
    return {
      state: 'inProgress',
      titleKey: 'chat.learningWelcome.inProgressTitle',
      bodyKey: 'chat.learningWelcome.inProgressBody',
      nextKey: context.next.type === 'COURSE_COMPLETED'
        ? 'chat.learningWelcome.finalStage'
        : 'chat.learningWelcome.next',
      params,
      percentage,
      showProgress: true,
      ...enhancements
    }
  }

  if (context.next.type === 'KNOWLEDGE_POINT') {
    return {
      state: 'completedNext',
      titleKey: 'chat.learningWelcome.completedTitle',
      bodyKey: 'chat.learningWelcome.completedWithNext',
      nextKey: '',
      params,
      percentage,
      showProgress: true,
      ...enhancements
    }
  }

  return {
    state: 'completedAll',
    titleKey: 'chat.learningWelcome.completedTitle',
    bodyKey: 'chat.learningWelcome.completedAll',
    nextKey: '',
    params,
    percentage,
    showProgress: true,
    ...enhancements
  }
}
